import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex } from 'components';
import { Button, Icon } from 'design-system-web';
import {
  AddProductToChargeDetails,
  ProductItem,
  ExternalPayerType,
} from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';

import { AddProductFormModal } from '../AddProductFormModal';
import { ChargeSummary } from '../ChargeSummary/ChargeSummary';
import { DraftItemsTable } from '../DraftItemsTable/DraftItemsTable';
import { InvoiceType } from 'graphql-query-contracts';

import styles from './DraftChargeEditor.scss';
import { DraftChargeConfirmationModal } from '../DraftChargeConfirmationModal/DraftChargeConfirmationModal';
import { useBillingPaymentsPermissions } from 'lane-shared/domains/billingPayments/hooks';

export function DraftChargeEditor({
  items,
  showEmpty,
  locale,
  currency,
  validation,
  payerId,
  payerType,
  handleSubmitDraftCharge,
  addDraftProductItem,
  updateDraftProductItem,
  deleteDraftProductItem,
  updateChargeDetails,
  isInvoiceOptionEnabled,
  invoiceType,
  channelId,
}: {
  items: ProductItem[];
  showEmpty: boolean;
  locale: SupportedLocaleEnum;
  currency: SupportedPaymentCurrency;
  validation?: string;
  payerId: string;
  payerType: ExternalPayerType;
  handleSubmitDraftCharge: () => Promise<void>;
  addDraftProductItem: (product: AddProductToChargeDetails) => void;
  updateDraftProductItem: (index: number, updatedProduct: ProductItem) => void;
  deleteDraftProductItem: (index: number) => void;
  updateChargeDetails: (value: InvoiceType | Date, fieldname: string) => void;
  isInvoiceOptionEnabled: boolean;
  invoiceType?: InvoiceType;
  channelId?: string;
}) {
  const { t } = useTranslation();
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [submitChargeModalOpen, setSubmitChargeModalOpen] = useState(false);

  const { canSubmitCharges } = useBillingPaymentsPermissions();

  const onAddProduct = (product: AddProductToChargeDetails) => {
    addDraftProductItem(product);
  };

  const handleSubmit = async () => {
    handleSubmitDraftCharge();
    setSubmitChargeModalOpen(false);

    const toastMessage =
      invoiceType === InvoiceType.InvoiceTypeMonthly
        ? t('abp.charges.chargeManager.draft.submitToast.monthly')
        : t('abp.charges.chargeManager.draft.submitToast');

    await window.Toast.show(toastMessage);
  };

  const addProductButton = (
    <Button
      startIcon={<Icon name="Plus" />}
      className={styles.AddProductButton}
      variant="tertiary"
      onClick={() => setAddProductModalOpen(true)}
      testId="add-product-button"
    >
      {t('abp.productsServices.table.add')}
    </Button>
  );

  return (
    <>
      {showEmpty && items.length === 0 ? (
        <Flex p={[2, 0, 0, 0]}>{addProductButton}</Flex>
      ) : (
        <Flex className={styles.DraftCharge} gap={5}>
          <DraftItemsTable
            items={items}
            locale={locale}
            addProductButton={addProductButton}
            updateDraftProductItem={updateDraftProductItem}
            deleteDraftProductItem={deleteDraftProductItem}
            currency={currency}
            channelId={channelId}
          />
          <ChargeSummary
            items={items}
            locale={locale}
            currency={currency}
          >
            {canSubmitCharges && items.length > 0 ? (
              <Button
                variant="primary"
                className={styles.SubmitChargeButton}
                onClick={() => setSubmitChargeModalOpen(true)}
                style={{ borderColor: 'transparent' }}
                testId="submit-charge-button"
                disabled={Boolean(validation)}
              >
                {t('abp.charges.chargeManager.draftCharge.submitChargeButton')}
              </Button>
            ) : undefined}
          </ChargeSummary>
        </Flex>
      )}
      <AddProductFormModal
        onAddProduct={onAddProduct}
        currency={currency}
        isOpen={addProductModalOpen}
        onClose={() => setAddProductModalOpen(false)}
        payerId={payerId}
        payerType={payerType}
        testId="add-product-form-modal"
      />
      <DraftChargeConfirmationModal
        isOpen={submitChargeModalOpen}
        updateChargeDetails={updateChargeDetails}
        onClose={() => setSubmitChargeModalOpen(false)}
        handleConfirm={handleSubmit}
        cancelText={t(
          'abp.charges.chargeManager.draftCharge.submitModal.cancelButton'
        )}
        confirmText={t(
          'abp.charges.chargeManager.draftCharge.submitModal.submitButton'
        )}
        title={t('abp.charges.chargeManager.draftCharge.submitModal.title')}
        description={t(
          'abp.charges.chargeManager.draftCharge.submitModal.description'
        )}
        isInvoiceOptionEnabled={isInvoiceOptionEnabled}
        invoiceType={invoiceType}
      />
    </>
  );
}
